import { useEffect, useLayoutEffect, useMemo, useRef } from "react";

/* Hover intent, the timers behind Popover's openOn="hover" (internal). A mouse
   resting on the anchor opens after `openDelayMs`; leaving anchor or content
   closes after `closeDelayMs`, and entering either again cancels that close.
   Touch, pen, coarse pointers and a held or pressed button never open it. */

/** The two fields read from a pointer event, native or React. */
export type HoverPointer = Pick<PointerEvent, "pointerType" | "buttons">;

export interface UseHoverIntentOptions {
  /** Off: every call does nothing and a pending timer is dropped. */
  enabled: boolean;
  open: boolean;
  openDelayMs: number;
  closeDelayMs: number;
  onOpenChange: (open: boolean) => void;
}

export interface HoverIntent {
  enter(e: HoverPointer): void;
  leave(e: HoverPointer): void;
  /** Focus reached the anchor: open now, unless skipFocus came first. */
  focus(): void;
  /** Focus left: close after the grace, unless a mouse is still over it. */
  blur(): void;
  /** A press on, or focus returning to, the anchor: that focus won't open. */
  skipFocus(): void;
  /** Hold it open until it closes. False when off or already pinned. */
  pin(): boolean;
}

const COARSE = "(hover: none), (pointer: coarse)";
const canHover = (e: HoverPointer): boolean =>
  e.pointerType === "mouse" && !window.matchMedia?.(COARSE).matches;
const IDLE = { timer: 0, inside: false, pinned: false, skip: false };

export function useHoverIntent(options: UseHoverIntentOptions): HoverIntent {
  const ref = useRef({ ...IDLE, options });
  useLayoutEffect(() => {
    ref.current.options = options;
    if (!options.open) ref.current.pinned = false;
  });
  const intent = useMemo<HoverIntent>(() => {
    const on = () => ref.current.options.enabled;
    const clear = () => window.clearTimeout(ref.current.timer);
    const set = (next: boolean) => {
      const s = ref.current;
      clear();
      if (!on() || s.options.open === next || (!next && s.pinned)) return;
      s.pinned = false;
      s.options.onOpenChange(next);
    };
    /* One timer: an open and a close are never pending together. */
    const schedule = (next: boolean) => {
      const { open, openDelayMs, closeDelayMs } = ref.current.options;
      clear();
      if (open === next || (!next && ref.current.pinned)) return;
      const ms = next ? openDelayMs : closeDelayMs;
      ref.current.timer = window.setTimeout(() => set(next), ms);
    };
    return {
      enter(e) {
        if (!on() || !canHover(e)) return;
        ref.current.inside = true;
        if (e.buttons === 0) schedule(true);
        else clear();
      },
      leave(e) {
        ref.current.skip = false;
        if (!on() || !canHover(e)) return;
        ref.current.inside = false;
        schedule(false);
      },
      focus() {
        if (!ref.current.skip) set(true);
        ref.current.skip = false;
      },
      blur() {
        ref.current.skip = false;
        if (on() && !ref.current.inside) schedule(false);
      },
      skipFocus() {
        clear();
        ref.current.skip = on();
      },
      pin() {
        if (!on() || ref.current.pinned) return false;
        clear();
        return (ref.current.pinned = true);
      },
    };
  }, []);
  useEffect(() => {
    const s = ref.current;
    return () => window.clearTimeout(s.timer);
  }, [options.enabled]); // turning it off, or unmounting, drops the timer
  return intent;
}
